import { useState } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { useUsers } from '../../hooks/useUsers';
import { useCreateGroup } from '../../hooks/useTeamChat';
import { useAuthStore } from '../../store/useAuthStore';
import { MemberPicker } from './MemberPicker';

/** Admin-only: create a group chat visible only to the selected members. */
export function CreateGroupModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (conversationId: string) => void;
}) {
  const { t } = useI18n();
  const me = useAuthStore((s) => s.user);
  const { data: users } = useUsers();
  const createGroup = useCreateGroup();
  const [name, setName] = useState('');
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  // The creator is force-included server-side; offer everyone else.
  const options = (users ?? [])
    .filter((u) => u.id !== me?.id)
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((u) => ({ id: u.id, name: u.name }));

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setError(null);
    createGroup.mutate(
      { name: trimmed, memberIds },
      {
        onSuccess: (conversation) => {
          setName('');
          setMemberIds([]);
          onCreated(conversation.id);
        },
        onError: (err) => setError(err instanceof Error ? err.message : String(err)),
      },
    );
  };

  return (
    <div
      className="ui-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={t('teamChat.newGroup')}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <form
        className="ui-modal-panel flex w-full max-w-md flex-col"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <h2 className="font-heading text-base font-semibold text-foreground">
          {t('teamChat.newGroup')}
        </h2>
        <label className="ui-field-label mt-3 block" htmlFor="team-chat-group-name">
          {t('teamChat.groupName')}
        </label>
        <input
          id="team-chat-group-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={100}
          className="ui-input"
          autoFocus
        />
        <span className="ui-field-label mt-3 block">{t('teamChat.members')}</span>
        {/* On a phone the panel is full screen: the list takes the free
            height, so the buttons sit at the bottom edge instead of
            leaving half a screen of nothing under them. */}
        <MemberPicker
          options={options}
          value={memberIds}
          onChange={setMemberIds}
          className="max-sm:min-h-0 max-sm:max-h-none max-sm:flex-1"
        />
        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-2 pt-5 max-sm:pb-[env(safe-area-inset-bottom)]">
          <button type="button" onClick={onClose} className="ui-btn-secondary">
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={!name.trim() || createGroup.isPending}
            className="ui-btn-primary disabled:opacity-50"
          >
            {t('common.create')}
          </button>
        </div>
      </form>
    </div>
  );
}
